import "./UserSettingsModal.scss";
import { useState, useRef, useEffect } from "react";
import ColorPicker from "../../../ColorPicker/ColorPicker";
import FlowTextInput from "../../../FlowTextInput/FlowTextInput";
import Notification from "../../../Notification/Notification";
import useAuthStore from "../../../../store/authStore";
import { fetcher } from "../../../../utils/fetcher";
import { normalizeColor } from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import LanguagePicker from "../../../LanguagePicker/LanguagePicker";

function UserSettingsModal({ onClose }) {
  const user = useAuthStore((state) => state.user);
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [selectedColor, setSelectedColor] = useState("cherry");
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  const firstNameInputRef = useRef(null);

  // Sync state with user data when it changes
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      // Normalize color to hex
      setSelectedColor(normalizeColor(user.profile?.color));
    }
  }, [user]);

  useEffect(() => {
    // Focus on first name input when modal opens
    if (firstNameInputRef.current) {
      firstNameInputRef.current.focus();
    }
  }, []);

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setNotification({
        message: t("settings.namesRequired"),
        type: "error",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Normalize color to hex
      const colorHex = normalizeColor(selectedColor);

      //   userId: user.id,
      //   firstName: firstName.trim(),
      //   lastName: lastName.trim(),
      //   color: selectedColor,
      //   colorHex,
      // });

      const updatedUser = await fetcher.put(`/api/auth/user/${user.id}`, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        profile: {
          ...user.profile,
          color: colorHex,
        },
      });

      // Update the user in the store
      useAuthStore.setState({
        user: {
          ...user,
          firstName: updatedUser.firstName,
          lastName: updatedUser.lastName,
          profile: updatedUser.profile,
        },
      });

      // Dispatch event to refresh task views
      window.dispatchEvent(new CustomEvent("userProfileUpdated"));

      onClose();
    } catch (error) {
      console.error("Error updating user settings:", error);
      console.error("Error details:", error.message, error.response);
      setNotification({
        message: t("settings.saveFailed", {
          error: error.message || t("common.tryAgain"),
        }),
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="user-settings-modal-content">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="title">
        <span>{t("settings.title")}</span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="main-wrapper">
        <div className="form-section">
          <div className="form-group">
            <label>{t("settings.firstName")}</label>
            <FlowTextInput
              inputRef={firstNameInputRef}
              placeholder={t("settings.firstNamePlaceholder")}
              value={firstName}
              onChange={(newValue) => setFirstName(newValue)}
              expandable={false}
              icon={null}
            />
          </div>

          <div className="form-group">
            <label>{t("settings.lastName")}</label>
            <FlowTextInput
              placeholder={t("settings.lastNamePlaceholder")}
              value={lastName}
              onChange={(newValue) => setLastName(newValue)}
              expandable={false}
              icon={null}
            />
          </div>

          <div className="form-group">
            <label>{t("settings.userColor")}</label>
            <div className="color-picker-wrapper">
              <ColorPicker
                selectedColor={selectedColor}
                onColorSelect={setSelectedColor}
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t("settings.language")}</label>
            <LanguagePicker />
          </div>
        </div>

        <div className="button-group">
          <button
            className="cancel-button"
            onClick={handleCancel}
            disabled={isSaving}
          >
            {t("common.cancel")}
          </button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserSettingsModal;
